import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Button,
  Paper,
} from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import PublicPageFrame from "../../../components/ui/PublicPageFrame";
import i18n from "../../../config/i18n";
import { useAppDispatch } from "../../../hooks/redux";
import { getApiRequestUrl } from "../../../services/sharedApi";
import { setAuth } from "../../../store/slices/authSlice";
import { logger } from "../../../utils/logger";
import {
  buildPostAuthCheckoutUrl,
  loadCheckoutIntent,
} from "../../payments/utils/checkoutIntent";
import { teacherInvitationPath } from "../../students/utils/invitationReturn";

/**
 * OAuth Callback Handler
 * Processes OAuth redirect callbacks from social providers
 * Extracts token from URL and authenticates user
 */
const OAuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation("auth");

  const [error, setError] = React.useState<string | null>(null);
  const [processing, setProcessing] = React.useState(true);

  useEffect(() => {
    const processCallback = async (): Promise<void> => {
      try {
        // Check for errors in URL
        const errorParam = searchParams.get("error");
        const needsLinking = searchParams.get("needsLinking");
        const provider = searchParams.get("provider");

        if (errorParam) {
          const errorMsg = decodeURIComponent(errorParam);

          if (needsLinking === "true") {
            // Redirect to login with linking message
            navigate("/auth", {
              state: {
                error: errorMsg,
                provider,
                needsLinking: true,
              },
            });
            return;
          }

          setError(errorMsg);
          setProcessing(false);
          return;
        }

        // Get token from URL
        const token = searchParams.get("token");
        const isNewUser = searchParams.get("isNewUser") === "true";

        if (!token) {
          setError(t("oauth.missingToken"));
          setProcessing(false);
          return;
        }

        // Fetch user data with token
        const response = await fetch(getApiRequestUrl("/auth/me"), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(i18n.t("auth:errors.failedToFetchUserData"));
        }

        const data = await response.json();

        // Set auth in Redux
        dispatch(
          setAuth({
            user: data.user,
            token,
          }),
        );

        // Redirect to appropriate page
        const invitation = teacherInvitationPath(
          searchParams.get("returnPath"),
        );
        if (invitation) {
          navigate(invitation, { replace: true });
          return;
        }
        const checkoutIntent = loadCheckoutIntent();
        if (checkoutIntent) {
          navigate(buildPostAuthCheckoutUrl(checkoutIntent), { replace: true });
          return;
        }

        if (isNewUser) {
          navigate("/home?welcome=true", { replace: true });
        } else {
          navigate("/home", { replace: true });
        }
      } catch (err) {
        logger.error("OAuth callback error:", err);
        setError(t("oauth.processingFailed"));
        setProcessing(false);
      }
    };

    processCallback();
  }, [searchParams, navigate, dispatch, t]);

  if (error) {
    return (
      <PublicPageFrame>
        <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "240px",
              gap: 3,
            }}
          >
            <Typography variant="h4" component="h1">
              {t("oauth.processingFailed")}
            </Typography>
            <Alert severity="error" sx={{ maxWidth: "sm" }}>
              {error}
            </Alert>
            <Button href="/auth" variant="contained">
              {t("oauth.backToLogin")}
            </Button>
          </Box>
        </Paper>
      </PublicPageFrame>
    );
  }

  return (
    <PublicPageFrame>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "240px",
            gap: 2,
          }}
        >
          <CircularProgress size={36} />
          <Typography variant="h5" component="h1">
            {processing ? t("oauth.processing") : t("oauth.redirecting")}
          </Typography>
        </Box>
      </Paper>
    </PublicPageFrame>
  );
};

export default OAuthCallbackPage;
